import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
    ArrowPathIcon,
    ArrowTopRightOnSquareIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    DocumentTextIcon,
    MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { SECTION_PERMISSIONS } from "@/config/permissions";
import { usePermissions } from "@/features/auth/usePermissions";
import { extractApiError } from "@/lib/api-error";

import { getRoadWorthinessQueue } from "../api/roadWorthinessApi";
import type {
    RoadWorthinessQueueParams,
    RoadWorthinessQueueStatus,
} from "../types";

const STATUS_OPTIONS: Array<{ value: RoadWorthinessQueueStatus | ""; label: string }> = [
    { value: "", label: "All" },
    { value: "PENDING", label: "Pending" },
    { value: "APPROVED", label: "Approved" },
    { value: "REJECTED", label: "Rejected" },
];

const APPROVAL_STATUS_STYLES: Record<RoadWorthinessQueueStatus, string> = {
    PENDING: "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300",
    APPROVED: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-300",
    REJECTED: "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300",
};

const formatDate = (value: string | null | undefined) => {
    if (!value) return "—";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat("en-NG", {
        dateStyle: "medium",
    }).format(date);
};

const formatDateTime = (value: string | null | undefined) => {
    if (!value) return "—";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat("en-NG", {
        dateStyle: "medium",
        timeStyle: "short",
    }).format(date);
};

const formatRelativeTime = (value: string | null | undefined) => {
    if (!value) return "—";

    const timestamp = new Date(value).getTime();
    if (Number.isNaN(timestamp)) return value;

    const diffMinutes = Math.round((Date.now() - timestamp) / 60_000);
    if (diffMinutes < 1) return "Updated now";
    if (diffMinutes < 60) return `Updated ${diffMinutes}m ago`;

    const diffHours = Math.round(diffMinutes / 60);
    if (diffHours < 24) return `Updated ${diffHours}h ago`;

    const diffDays = Math.round(diffHours / 24);
    return `Updated ${diffDays}d ago`;
};

const getValidityLabel = (expiryDate: string, isValid: boolean) => {
    const expiry = new Date(expiryDate).getTime();
    if (!Number.isNaN(expiry) && expiry < Date.now()) return "Expired";
    return isValid ? "Valid" : "Invalid";
};

const getValidityTone = (expiryDate: string, isValid: boolean) => {
    const label = getValidityLabel(expiryDate, isValid);
    if (label === "Expired") {
        return "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300";
    }
    if (label === "Valid") {
        return "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-300";
    }
    return "border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300";
};

export const RoadWorthinessRoute = () => {
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const canReviewRoadWorthiness = hasPermission(
        SECTION_PERMISSIONS.DRIVER_ROAD_WORTHINESS_REVIEW[0]
    );

    const [filters, setFilters] = useState<{
        page: number;
        size: number;
        search: string;
        approvalStatus: RoadWorthinessQueueStatus | "";
    }>({
        page: 0,
        size: 20,
        search: "",
        approvalStatus: "",
    });

    const queryParams = useMemo<RoadWorthinessQueueParams>(
        () => ({
            page: filters.page,
            size: filters.size,
            search: filters.search.trim() || undefined,
            approvalStatus: filters.approvalStatus,
        }),
        [filters]
    );

    const { data, isLoading, error, isFetching, refetch } = useQuery({
        queryKey: ["road-worthiness-queue", queryParams],
        queryFn: () => getRoadWorthinessQueue(queryParams),
    });

    const queue = data;
    const items = queue?.items ?? [];

    const setFilter = <TKey extends keyof typeof filters>(
        key: TKey,
        value: (typeof filters)[TKey]
    ) => {
        setFilters((previous) => ({
            ...previous,
            [key]: value,
            ...(key === "page" ? {} : { page: 0 }),
        }));
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-24">
                <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-700 dark:border-rose-900/60 dark:bg-rose-900/20 dark:text-rose-300">
                {extractApiError(error)}
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-50">
                            Road worthiness queue
                        </h2>
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                            Review driver road-worthiness documents and open driver profiles to
                            approve or reject them.
                        </p>
                    </div>
                    <div className="text-sm text-slate-500 dark:text-slate-400">
                        {queue?.totalElements ?? items.length} records
                    </div>
                </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_140px_auto]">
                    <label className="space-y-2 text-sm">
                        <span className="font-medium text-slate-700 dark:text-slate-200">
                            Driver
                        </span>
                        <div className="relative">
                            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                            <input
                                value={filters.search}
                                onChange={(event) => setFilter("search", event.target.value)}
                                placeholder="Name, email or phone"
                                className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-4 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                            />
                        </div>
                    </label>

                    <label className="space-y-2 text-sm">
                        <span className="font-medium text-slate-700 dark:text-slate-200">
                            Rows
                        </span>
                        <select
                            value={filters.size}
                            onChange={(event) => setFilter("size", Number(event.target.value))}
                            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                        >
                            {[10, 20, 50].map((size) => (
                                <option key={size} value={size}>
                                    {size}
                                </option>
                            ))}
                        </select>
                    </label>

                    <div className="flex flex-wrap items-end gap-3 lg:justify-end">
                        <Button
                            variant="outline"
                            onClick={() =>
                                setFilters({
                                    page: 0,
                                    size: 20,
                                    search: "",
                                    approvalStatus: "",
                                })
                            }
                        >
                            Reset
                        </Button>
                        <Button variant="outline" onClick={() => refetch()}>
                            <ArrowPathIcon className="mr-2 h-4 w-4" />
                            Refresh
                        </Button>
                    </div>
                </div>

                <div className="mt-4 flex flex-col gap-2 lg:flex-row lg:items-center">
                    <span className="shrink-0 text-sm font-medium text-slate-700 dark:text-slate-200">
                        Status
                    </span>
                    <div className="flex flex-wrap gap-2">
                        {STATUS_OPTIONS.map((option) => {
                            const active = filters.approvalStatus === option.value;
                            return (
                                <button
                                    key={option.label}
                                    type="button"
                                    onClick={() => setFilter("approvalStatus", option.value)}
                                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                                        active
                                            ? "border-slate-900 bg-slate-900 text-white dark:border-slate-100 dark:bg-slate-100 dark:text-slate-900"
                                            : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:border-slate-600 dark:hover:text-slate-100"
                                    }`}
                                >
                                    {option.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="mt-2 flex items-center justify-between gap-3 text-xs text-slate-500 dark:text-slate-400">
                    <span>
                        {filters.approvalStatus ? filters.approvalStatus : "All statuses"}
                    </span>
                    {isFetching ? <span>Refreshing...</span> : null}
                </div>
            </section>

            <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-800">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                        Queue
                    </h3>
                    {isFetching ? (
                        <div className="inline-flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                            <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                            Refreshing
                        </div>
                    ) : null}
                </div>

                {items.length === 0 ? (
                    <div className="px-6 py-14 text-center">
                        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
                            <DocumentTextIcon className="h-6 w-6 text-slate-400" />
                        </div>
                        <h4 className="mt-4 text-base font-semibold text-slate-900 dark:text-slate-100">
                            No records found
                        </h4>
                        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                            Adjust the filters and try again.
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-800">
                            <thead className="bg-slate-50 dark:bg-slate-950/50">
                                <tr className="text-left text-xs uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                                    <th className="px-6 py-3.5 font-medium">Driver</th>
                                    <th className="px-6 py-3.5 font-medium">Certificate</th>
                                    <th className="px-6 py-3.5 font-medium">Approval</th>
                                    <th className="px-6 py-3.5 font-medium">Validity</th>
                                    <th className="px-6 py-3.5 font-medium">Expiry</th>
                                    <th className="px-6 py-3.5 font-medium">Document</th>
                                    <th className="px-6 py-3.5 font-medium">Updated</th>
                                    <th className="px-6 py-3.5 font-medium text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {items.map((item) => (
                                    <tr
                                        key={item.driverId}
                                        className="hover:bg-slate-50/80 dark:hover:bg-slate-950/30"
                                    >
                                        <td className="px-6 py-4 align-top">
                                            <div className="space-y-1">
                                                <button
                                                    type="button"
                                                    onClick={() => navigate(`/drivers/${item.driverId}`)}
                                                    className="text-left font-semibold text-slate-900 hover:text-primary-600 dark:text-slate-100 dark:hover:text-primary-400"
                                                >
                                                    {[item.firstName, item.lastName]
                                                        .filter(Boolean)
                                                        .join(" ")}
                                                </button>
                                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                                    {item.email || item.phoneNumber}
                                                </p>
                                                <p className="font-mono text-[11px] text-slate-400 dark:text-slate-500">
                                                    {item.driverId}
                                                </p>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 align-top">
                                            <div className="space-y-1">
                                                <p className="font-mono text-sm font-semibold text-slate-900 dark:text-slate-100">
                                                    {item.certificateNumber}
                                                </p>
                                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                                    Issued {formatDate(item.issueDate)}
                                                </p>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 align-top">
                                            <span
                                                className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${APPROVAL_STATUS_STYLES[item.approvalStatus]}`}
                                            >
                                                {item.approvalStatus}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 align-top">
                                            <span
                                                className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${getValidityTone(
                                                    item.expiryDate,
                                                    item.isValid
                                                )}`}
                                            >
                                                {getValidityLabel(item.expiryDate, item.isValid)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 align-top">
                                            <div className="space-y-1 text-xs text-slate-500 dark:text-slate-400">
                                                <p className="font-medium text-slate-900 dark:text-slate-100">
                                                    {formatDate(item.expiryDate)}
                                                </p>
                                                <p>Status: {item.driverStatus}</p>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 align-top">
                                            {item.documentUrl ? (
                                                <a
                                                    href={item.documentUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400"
                                                >
                                                    Open
                                                    <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                                                </a>
                                            ) : (
                                                <span className="text-sm text-slate-400 dark:text-slate-500">
                                                    —
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 align-top">
                                            <div className="space-y-1 text-xs text-slate-500 dark:text-slate-400">
                                                <p className="font-medium text-slate-900 dark:text-slate-100">
                                                    {formatDateTime(item.updatedAt)}
                                                </p>
                                                <p>{formatRelativeTime(item.updatedAt)}</p>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 align-top">
                                            <div className="flex items-center justify-end gap-2">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => navigate(`/drivers/${item.driverId}`)}
                                                >
                                                    {canReviewRoadWorthiness ? "Review" : "View"}
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="flex flex-col gap-4 border-t border-slate-200 px-6 py-4 sm:flex-row sm:items-center sm:justify-between dark:border-slate-800">
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                        Showing {items.length} of {queue?.totalElements ?? items.length} records
                    </p>
                    <div className="flex items-center gap-3">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setFilter("page", Math.max(filters.page - 1, 0))}
                            disabled={!queue?.hasPrevious}
                        >
                            <ChevronLeftIcon className="mr-1 h-4 w-4" />
                            Previous
                        </Button>
                        <span className="text-sm text-slate-600 dark:text-slate-300">
                            Page {(queue?.page ?? 0) + 1} of {queue?.totalPages ?? 1}
                        </span>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setFilter("page", (queue?.page ?? 0) + 1)}
                            disabled={!queue?.hasNext}
                        >
                            Next
                            <ChevronRightIcon className="ml-1 h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </section>
        </div>
    );
};
